/**
 * Centralised endpoint resolver for role management.
 *
 * Roles, the users that hold them, and their permissions are managed per
 * center via the center-scoped API:
 *
 *   GET    /centers/{center_id}/roles                 list roles
 *   POST   /centers/{center_id}/roles                 create a role
 *   GET    /centers/{center_id}/roles/{role_id}       read one role + permissions
 *   PUT    /centers/{center_id}/roles/{role_id}       update a role + permissions
 *   DELETE /centers/{center_id}/roles/{role_id}       delete a role
 *   GET    /centers/{center_id}/roles/{role_id}/users users holding the role
 *
 * The request/response contract mirrors the global /admin/roles endpoint
 * (same backend), so callers parse the response the same way.
 *
 * When no center is in scope (e.g. a super admin whose session isn't pinned to
 * a single center) we fall back to the global /admin/roles endpoint.
 */
import { API_ENDPOINTS } from 'constants/apiEndpoints';
import { STORAGE_KEYS } from 'constants/storageKeys';
import { storage } from 'util/storage';

// Kept inline (not imported from util/access) to avoid a circular import —
// util/access already imports getValidCenterId from this module. Mirrors
// util/access's SUPER_ADMIN_ROLES: superadmin / super_admin / super admin / master.

/**
 * The signed-in center — taken from the persisted user profile, which mirrors
 * the center the JWT is scoped to (a super admin who switches center via the
 * header re-mints the token and the profile updates to match).
 *
 * NOTE: a legacy `localStorage['centerId']` override was removed here. Nothing in
 * the app writes that key, so a stale value left in a browser (from an older
 * build) would silently pin every center-scoped request — list filters AND the
 * program-create payload — to the wrong center, causing "Cannot create programs
 * for another center" and empty lists. Always trusting the session/token center
 * removes that footgun.
 */
export const getValidCenterId = () => {
  const user = storage.get(STORAGE_KEYS.USER) || {};
  const id = user.center_id ?? user.centerId ?? user.center?.id ?? null;
  return id != null && String(id) !== '' ? String(id) : null;
};

/** True when the signed-in user is a platform super admin / master. */
const isPlatformSuperAdmin = () => {
  const user = storage.get(STORAGE_KEYS.USER) || {};
  if (user.is_platform_admin) return true;
  const r = String(user.role || storage.get(STORAGE_KEYS.ROLE) || '')
    .toLowerCase()
    .replace(/[_\s-]/g, '');
  return r === 'superadmin' || r === 'master';
};

/**
 * Base path for the role collection. A super admin / master ALWAYS manages the
 * platform role catalog (`/admin/roles`) — the 6 canonical platform roles — even
 * if their session happens to carry a center_id; otherwise they'd wrongly see a
 * single center's custom designations (Design/Finance/Operation Manager, …).
 * A center admin uses their center-scoped catalog.
 */
export const rolesBasePath = () => {
  if (isPlatformSuperAdmin()) return API_ENDPOINTS.ADMIN.ROLES;
  const centerId = getValidCenterId();
  return centerId
    ? `/centers/${encodeURIComponent(centerId)}/roles`
    : API_ENDPOINTS.ADMIN.ROLES;
};

/**
 * Build a role endpoint path. Pass a suffix such as `/${roleId}` or
 * `/${roleId}/users`; defaults to the collection path.
 */
export const rolePath = (suffix = '') => `${rolesBasePath()}${suffix}`;

/**
 * True when the Role Editor is managing a CENTER'S TEAM roles rather than the
 * platform role catalog. Center-team-only rules (e.g. Programs write implying
 * the evaluation modules) hang off this — the backend gates on the same thing,
 * `roles.center_id` being set.
 */
export const isCenterScopedRoles = () => rolesBasePath().startsWith('/centers/');

export default rolePath;
